import React from 'react';
import { Box, IconButton, Paper, Typography, styled } from '@mui/material';
import { Close } from '@mui/icons-material';
import { MuiModal } from 'ui/MuiModal';

export interface ChoiceModalOption {
    id: string;
    icon: React.ReactNode;
    label: string;
    description: string;
    onClick: () => void;
}

interface ChoiceModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    subtitle: string;
    options: ChoiceModalOption[];
}

const StyledMuiModal = styled(MuiModal)(({ theme }) => ({
    width: `calc(100% - ${theme.spacing(4)})`,
    maxWidth: '640px'
}));

const OptionCard = styled(Paper)(({ theme }) => ({
    flex: 1,
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    transition: theme.transitions.create(['border-color', 'background-color']),
    '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.hover
    }
}));

export const ChoiceModal: React.FC<ChoiceModalProps> = ({ open, onClose, title, subtitle, options }) => {
    const handleOptionClick = React.useCallback(
        (option: ChoiceModalOption) => () => {
            option.onClick();
        },
        []
    );

    return (
        <StyledMuiModal open={open} onClose={onClose}>
            <Box sx={{ p: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                    <Typography variant="h5" fontWeight="bold">
                        {title}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    {subtitle}
                </Typography>
                <Box display="flex" gap={2}>
                    {options.map(option => (
                        <OptionCard key={option.id} onClick={handleOptionClick(option)}>
                            <Box sx={{ color: 'text.secondary', fontSize: 32, lineHeight: 0 }}>
                                {option.icon}
                            </Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {option.label}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {option.description}
                            </Typography>
                        </OptionCard>
                    ))}
                </Box>
            </Box>
        </StyledMuiModal>
    );
};
